import { Component, createRef } from "react";
import { connect } from "react-redux";

import { endExportChartData, exportAsPicture } from "../../redux/modules/data-export.js";
import { type IAppState } from "../../redux/reducer.js";

interface IPixelatorOwnProps {
  width: number;
  height: number;
  getNodeRef: () => any;
  oversampling?: number;
}

interface IPixelatorAppStateProps {
  shouldExportChart: boolean;
  exportChartFileName: string;
}

interface IPixelatorDispatchProps {
  disableChartExport: (error?: string) => void;
  exportAsPicture: (canvasRef: any, name: string) => void;
}

interface IPixelatorProps extends IPixelatorOwnProps, IPixelatorAppStateProps, IPixelatorDispatchProps {}

function mapStateToProps(state: IAppState): IPixelatorAppStateProps {
  return {
    shouldExportChart: state.dataExport.shouldExportChart,
    exportChartFileName: state.dataExport.exportChartFileName,
  };
}

function mapDispatchToProps(dispatch): IPixelatorDispatchProps {
  return {
    disableChartExport: (error?: string) => dispatch(endExportChartData(error)),
    exportAsPicture: (canvasRef: any, name: string) => dispatch(exportAsPicture(canvasRef, name)),
  };
}

export const CHART_LEGEND_OPTION_HEIGHT = 25;

/**
 * Renders a SVG graphic into a PNG pixel graphic.
 * This 'smart' component
 *
 * - is invisible.
 *
 * - is connected to dataExport of IAppState and the data-export.ts (actions, action generators, and reducers)
 *
 * - for shouldExportChart == false, the component is a single empty <div>;
 *   otherwise, this component allocates a w * h pixel image
 *   where w and h are oversampling * this.props.width and oversampling * this.props.height, respectively.
 *
 * created on 4/27/2017
 */
class Pixelator extends Component<IPixelatorProps> {
  private canvasNodeRef = createRef<HTMLCanvasElement>();

  constructor(props: IPixelatorProps) {
    super(props);
    this.exportAndDeactivate = this.exportAndDeactivate.bind(this);
    this.exportChart = this.exportChart.bind(this);
    this.getOversampling = this.getOversampling.bind(this);
    this.extractSVGFromComposedChartRef = this.extractSVGFromComposedChartRef.bind(this);
  }

  // called just after the component has been rendered for the very first time
  componentDidMount() {
    setTimeout(this.exportAndDeactivate, 0);
  }

  // called just after the component has been rendered (except for the very first time)
  componentDidUpdate() {
    setTimeout(this.exportAndDeactivate, 0);
  }

  render() {
    return (
      <div style={{ visibility: "hidden", width: 0, height: 0, position: "absolute" }}>
        <canvas
          className="pixelator"
          ref={this.canvasNodeRef}
          width={Math.floor(this.getOversampling() * this.props.width)}
          height={Math.floor(this.getOversampling() * this.props.height)}
        />
      </div>
    );
  }

  exportAndDeactivate() {
    if (this.props.width && this.props.shouldExportChart) {
      try {
        this.exportChart();
        this.props.disableChartExport();
      } catch (error) {
        this.props.disableChartExport((error as Error).message);
      }
    }
  }

  // non overwriting methods >>
  getOversampling() {
    return this.props.oversampling ? this.props.oversampling : 2.0;
  }

  exportChart() {
    const serializedSVG = this.extractSVGFromComposedChartRef();
    const canvas = this.canvasNodeRef.current;

    if (!serializedSVG || !canvas) {
      return;
    }

    const renderOptions = {
      scaleWidth: this.getOversampling() * this.props.width,
      scaleHeight: this.getOversampling() * this.props.height,
    };
    const context2d = canvas.getContext("2d");
    context2d.imageSmoothingEnabled = false;
    context2d.clearRect(0, 0, renderOptions.scaleWidth, renderOptions.scaleHeight);
    // render white background
    context2d.fillStyle = "white";
    context2d.fillRect(0, 0, renderOptions.scaleWidth, renderOptions.scaleHeight);

    const image = new Image(renderOptions.scaleWidth, renderOptions.scaleHeight);
    const svg = new Blob([serializedSVG], { type: "image/svg+xml" });
    const fileName = this.props.exportChartFileName;

    image.onload = () => {
      context2d.drawImage(image, 0, 0, renderOptions.scaleWidth, renderOptions.scaleHeight);
      this.props.exportAsPicture(canvas, fileName);
    };

    image.src = URL.createObjectURL(svg);
  }

  /**
   * Extracts the SVG string (XML content) from a Rechart ComposedChart element.
   * Manual transfer of some style attributes to make the resulting chart appear more
   * similar to the on-screen version. This mainly concerns the axis labels.
   * Works with Chrome, Firefox, and IE10.
   */
  extractSVGFromComposedChartRef(): string {
    const node = this.props.getNodeRef();

    // node.container refers to the recharts-wrapper DIV DOM Element
    if (node?.childNodes[0]?.childNodes[0]) {
      const svgNode = node.childNodes[0]?.childNodes[0].cloneNode(true);
      const legendNode = node.childNodes[0].childNodes[1].cloneNode(true);

      // create a style node inferred from the original computed style, so that the text does not need the css
      const styleNode = document.createElementNS("http://www.w3.org/2000/svg", "style");
      const htmlCss = getComputedStyle(node.childNodes[0]);

      let svgCss = ".axis-label, .legend, .recharts-cartesian-axis-tick { stroke: none;";
      svgCss += "font-family: " + htmlCss.fontFamily.toString() + ";";
      // svgCss += 'letter-spacing: "1.6px";'; // does not work in chrome
      svgCss += "font-size: " + this.getOversampling() * 9 + ";";
      svgCss += "fill: " + htmlCss.color.toString() + ";";
      svgCss += "}";
      styleNode.appendChild(document.createTextNode(svgCss));

      // compute the height of the legend
      const dataSeriesCount: number = legendNode.firstChild.firstChild.childNodes.length; // SEL6-1714: additional ".firstChild" because of new recharts version, where legend looks like
      // <div  class="recharts-legend-wrapper" style="..."><svg xmlns="http://www.w3.org/2000/svg"><g transform="translate(50,10)"> ...
      const legendHeight: number = dataSeriesCount * CHART_LEGEND_OPTION_HEIGHT;
      const updateSvgNodeHeight: number = Number.parseInt(svgNode.getAttribute("height"), 10) + legendHeight;

      // create svg node from scratch e.g.
      //   <svg class="recharts-surface" width="1220" height="568" viewBox="0 0 1220 568" version="1.1">
      const exportSvgNode = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      exportSvgNode.setAttribute("version", "1.1");
      exportSvgNode.setAttribute("width", svgNode.getAttribute("width"));
      exportSvgNode.setAttribute("height", `${updateSvgNodeHeight}`);
      exportSvgNode.setAttribute("viewBox", `0 0 ${svgNode.getAttribute("width")} ${updateSvgNodeHeight}`);

      exportSvgNode.appendChild(styleNode);

      // create the legend wrapper node with the original legend node inside
      const legenWrapperNode = document.createElementNS("http://www.w3.org/2000/svg", "g");
      legenWrapperNode.setAttribute("transform", "translate(30 50)");
      legenWrapperNode.appendChild(legendNode.firstChild.firstChild); // Append the Group, not the SVG
      // add the legend to the node
      exportSvgNode.appendChild(legenWrapperNode);

      // create a container that transform all other nodes to stick to the bottom (except the legend node and style node)
      const childNodesWrapper = document.createElementNS("http://www.w3.org/2000/svg", "g");
      childNodesWrapper.setAttribute("transform", `translate(0 ${legendHeight})`);
      // append all child nodes of the original chart node
      for (const childNode of svgNode.childNodes) {
        childNodesWrapper.appendChild(childNode.cloneNode(true));
      }
      // ..and append of this wrapper to the svg node to be exported
      exportSvgNode.appendChild(childNodesWrapper);

      return new XMLSerializer().serializeToString(exportSvgNode);
    }

    throw new Error("Cannot extract SVG content from chart node");
  }
}

//<div class="recharts-legend-wrapper" style="position: absolute; width: 1160px; height: auto; left: 30px; top: 50px;"><g class="recharts-default-legend" style="padding: 0px; margin: 0px; text-align: center;"><g class="recharts-legend-item legend-item-0" style="display: inline-block; margin-right: 10px;">BG046802</g></g></div>
//<div xmlns="http://www.w3.org/1999/xhtml" class="recharts-legend-wrapper" style="position: absolute; width: 1114px; height: auto; left: 30px; top: 194.5px; display: none;"><svg xmlns="http://www.w3.org/2000/svg"><g transform="translate(50,10)"><text class="legend" text-anchor="start" alignment-baseline="before-edge"><tspan y="-3" fill="rgb(213, 62, 79)" font-size="20px">■ </tspan><tspan y="0" x="30">PsAffx.psZS001xP06f Strain Conrad</tspan></text><text class="legend" text-anchor="start" alignment-baseline="before-edge"><tspan y="22" fill="rgb(224, 110, 123)" font-size="20px">■ </tspan><tspan y="25" x="30">PsAffx.psZS001xP06f Strain Sloan</tspan></text><text class="legend" text-anchor="start" alignment-baseline="before-edge"><tspan y="47" fill="rgb(234, 159, 167)" font-size="20px">■ </tspan><tspan y="50" x="30">PsAffx.psZS001xP06f Strain V71-370</tspan></text><text class="legend" text-anchor="start" alignment-baseline="before-edge"><tspan y="72" fill="rgb(245, 207, 211)" font-size="20px">■ </tspan><tspan y="75" x="30">PsAffx.psZS001xP06f Strain VP-RIL9</tspan></text></g></svg></div>
// <div  class="recharts-legend-wrapper" style="..."><svg xmlns="http://www.w3.org/2000/svg"><g transform="translate(50,10)"> ...
export default connect<IPixelatorAppStateProps, IPixelatorDispatchProps, IPixelatorOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(Pixelator);
